import * as React from 'react'
import moment from 'moment'
import { faFileLinesSolid, FaIcon, faTableSolid } from '@/lib/fa'
import { SelectionGroup } from '@/modules/drivers/context/TableContext'
import { useTask } from '@/modules/shared/context/TaskContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import usePrintService from '@/modules/drivers/services/PrintService'
import { ReportType } from '@/app/constants/reportType'
import { TaskType } from '@/app/constants/taskType'
import { SERVICE_TIME_FORMAT } from '@/app/constants/date'
import { toast } from '@/lib/toasts/api'
import { useModals } from '@/modules/drivers/stores/modalsStore'
import { useSideSelectionStore } from '@/modules/drivers/stores/sideSelectionStore'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'

const PrintActions = ({
  companyId,
  onOpenGroupedContractPrint,
}: {
  companyId: string
  onOpenGroupedContractPrint: () => void
}) => {
  const { t } = useLocalize()
  const { sideCheckedIds, sideIsActive } = useSideSelectionStore()
  const selectedGroup: SelectionGroup =
    sideCheckedIds.length > 0
      ? { ids: sideCheckedIds, isActive: sideIsActive }
      : null
  const { getPdf } = usePrintService()
  const { addTask } = useTask()
  const { setIsPrintActionsOpen } = useModals()
  const [isPrinting, setIsPrinting] = React.useState(false)
  const { isGenerating, busyTooltip, runPrintJob } = usePrintJobGuard()

  const printTable = React.useCallback(async () => {
    if (isPrinting) return
    const driverIds = (selectedGroup?.ids ?? []).map((id) => String(id))

    if (driverIds.length === 0) {
      toast.warning({ text: t('driverModule.select-drivers') })
      return
    }

    try {
      setIsPrinting(true)
      const startTime = moment()
        .subtract(1, 'year')
        .startOf('day')
        .format(SERVICE_TIME_FORMAT)
      const endTime = moment().endOf('day').format(SERVICE_TIME_FORMAT)

      const taskId = await getPdf({
        companyId,
        reportType: ReportType.DRIVERS_LIST,
        drivers: driverIds,
        startTime,
        endTime,
        data: { separateFiles: false },
      })

      addTask(taskId, TaskType.Printout, ReportType.DRIVERS_LIST)
      setIsPrintActionsOpen(false)
    } catch (error) {
      console.error('Error printing drivers list:', error)
      toast.error({ text: t('printout.generation-error') })
    } finally {
      setIsPrinting(false)
    }
  }, [
    selectedGroup,
    companyId,
    getPdf,
    addTask,
    t,
    isPrinting,
    setIsPrintActionsOpen,
  ])

  const handlePrintTable = React.useCallback(
    () => runPrintJob(printTable),
    [printTable, runPrintJob],
  )

  const ACTIONS = [
    {
      label: 'Drukuj tabele',
      onClick: handlePrintTable,
      icon: faTableSolid,
      blocked: isPrinting || isGenerating,
    },
    {
      label: t('printout.driverContractsBulkPrint'),
      onClick: onOpenGroupedContractPrint,
      icon: faFileLinesSolid,
      blocked: isGenerating,
    },
  ]

  return (
    <div className="tw-absolute tw-mt-4 tw-flex tw-min-w-[230px] tw-flex-col tw-gap-1.5 tw-rounded tw-border tw-bg-white tw-p-1.5">
      {ACTIONS.map((action) => {
        return (
          <button
            key={action.label}
            type="button"
            onClick={action.blocked ? undefined : action?.onClick}
            title={action.blocked ? busyTooltip : undefined}
            className={`tw-flex tw-gap-2 tw-rounded tw-p-1.5 hover:tw-bg-base-200 ${
              action.blocked ? 'tw-cursor-not-allowed tw-opacity-50' : ''
            }`}
          >
            <FaIcon icon={action?.icon} />
            <p className="tw-color-content-base tw-text-sm tw-font-medium">
              {action?.label}
            </p>
          </button>
        )
      })}
    </div>
  )
}

export default PrintActions
